/**
 * Sojern / GTM dataLayer pushes for the headless `/book` funnel.
 * @see docs/Sojern/sojern-book-page-implementation-plan.md (Phases 2–4)
 */

export type SojernBookPageName = "rooms" | "addons" | "details" | "confirmation";

export type SojernBookGtmEvent =
  | "brs_book_rooms"
  | "brs_book_addons"
  | "brs_book_details"
  | "brs_book_confirmation";

export type SojernBookDataLayerPush = {
  event: SojernBookGtmEvent;
  pageName: SojernBookPageName;
  propertyID: string;
  checkInDate: string;
  checkOutDate: string;
  city: string;
  state: string;
  country: string;
  numberOfNights?: number;
  numberOfRooms?: number;
  numberOfTravelers?: number;
  price?: string;
  currency?: string;
  confirmationNumber?: string;
};

const GTM_EVENT_BY_PAGE: Record<SojernBookPageName, SojernBookGtmEvent> = {
  rooms: "brs_book_rooms",
  addons: "brs_book_addons",
  details: "brs_book_details",
  confirmation: "brs_book_confirmation",
};

function mewsHotelId(): string | null {
  const raw = import.meta.env.VITE_MEWS_HOTEL_ID;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

function staticGeo(): { city: string; state: string; country: string } {
  const city = String(import.meta.env.VITE_PROPERTY_CITY ?? "").trim();
  const state = String(import.meta.env.VITE_PROPERTY_STATE ?? "").trim();
  const country = String(import.meta.env.VITE_PROPERTY_COUNTRY ?? "").trim();
  return { city, state, country };
}

/** Glossary: no grouping, no currency symbol; variable decimals (e.g. 1234.5). */
export function formatSojernPrice(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "";
  return new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  }).format(value);
}

function pushPayload(payload: SojernBookDataLayerPush): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload as Record<string, unknown>);
  if (import.meta.env.DEV) {
    console.debug("[Sojern/book]", payload);
  }
}

export type PushSojernBookEventArgs = {
  pageName: SojernBookPageName;
  checkInDate: string;
  checkOutDate: string;
  numberOfNights?: number;
  numberOfRooms?: number;
  numberOfTravelers?: number;
  price?: number;
  currency?: string;
  confirmationNumber?: string;
};

/**
 * Pushes one `brs_book_*` object to `dataLayer`. Returns false if required fields are missing
 * (no push). `propertyID` comes from `VITE_MEWS_HOTEL_ID`.
 */
export function pushSojernBookEvent(args: PushSojernBookEventArgs): boolean {
  const propertyID = mewsHotelId();
  if (!propertyID) return false;

  const checkInDate = args.checkInDate?.trim();
  const checkOutDate = args.checkOutDate?.trim();
  if (!checkInDate || !checkOutDate) return false;

  const geo = staticGeo();
  const base: SojernBookDataLayerPush = {
    event: GTM_EVENT_BY_PAGE[args.pageName],
    pageName: args.pageName,
    propertyID,
    checkInDate,
    checkOutDate,
    city: geo.city,
    state: geo.state,
    country: geo.country,
  };

  if (args.numberOfNights != null && args.numberOfNights >= 0) {
    base.numberOfNights = args.numberOfNights;
  }
  if (args.numberOfRooms != null && args.numberOfRooms >= 1) {
    base.numberOfRooms = args.numberOfRooms;
  }
  if (args.numberOfTravelers != null && args.numberOfTravelers >= 1) {
    base.numberOfTravelers = args.numberOfTravelers;
  }

  if (args.pageName === "details" || args.pageName === "confirmation") {
    const cur = args.currency?.trim().toUpperCase();
    if (args.price == null || !Number.isFinite(args.price) || !cur) return false;
    base.price = formatSojernPrice(args.price);
    base.currency = cur;
  }

  if (args.pageName === "confirmation") {
    const cn = args.confirmationNumber?.trim();
    if (!cn) return false;
    base.confirmationNumber = cn;
  }

  pushPayload(base);
  return true;
}
